import { apiClient, idempotentConfig, unwrap } from './client';
import type {
  ApiEnvelope,
  BindTriggerRequest,
  FlowBindingKind,
  FlowBindingView,
  FlowBindingsView,
} from './types';

/**
 * Trigger bindings (OPSFL-FLOWOPS-1 A2) — the MODULE-OWNED `/v1/flow-bindings` surface over the
 * lifecycle `lifecycle_flow_binding` registry: which PUBLISHED flow version a CLOCK type / SIGNAL
 * type / API name starts. Reads ride `flows.author`; mutations assert `flows.bindings.write`
 * (hub_admin, flow_designer) and carry `ol-idempotency-key`. Not in the ratified product-api.v1
 * contract (proposed for a future OpenAPI fragment) — the client mirrors the controller shape.
 */

/** GET /v1/flow-bindings?flowId= — rows (enabled + kept-disabled), UNBOUND sightings, catalogue. */
export async function listFlowBindings(flowId?: string): Promise<FlowBindingsView> {
  const { data } = await apiClient.get<ApiEnvelope<FlowBindingsView>>('/v1/flow-bindings', {
    params: flowId ? { flowId } : undefined,
  });
  return unwrap(data);
}

/**
 * POST /v1/flow-bindings — bind (upsert) `(kind, typeKey)` → `flowId@version` (201). The target
 * must be a published, non-archived version: 422 BINDING_TARGET_NOT_PUBLISHED otherwise; 404 for an
 * unknown flow/version; 400 on kind/key grammar; 503 when lifecycle-clocks is off.
 */
export async function bindTrigger(
  req: BindTriggerRequest,
  idemKey?: string,
): Promise<FlowBindingView> {
  const body: BindTriggerRequest = { kind: req.kind, typeKey: req.typeKey, flowId: req.flowId };
  if (req.version != null) body.version = req.version;
  const { data } = await apiClient.post<ApiEnvelope<FlowBindingView>>(
    '/v1/flow-bindings',
    body,
    idempotentConfig(idemKey),
  );
  return unwrap(data);
}

/** POST /v1/flow-bindings/{kind}/{typeKey}/unbind — disable; the row is kept (200). */
export async function unbindTrigger(
  kind: FlowBindingKind,
  typeKey: string,
  idemKey?: string,
): Promise<FlowBindingView> {
  const { data } = await apiClient.post<ApiEnvelope<FlowBindingView>>(
    `/v1/flow-bindings/${encodeURIComponent(kind)}/${encodeURIComponent(typeKey)}/unbind`,
    {},
    idempotentConfig(idemKey),
  );
  return unwrap(data);
}

/** The closed key grammar the server enforces (upper-snake operator name, ≤ 64 chars). */
export const TRIGGER_KEY_PATTERN = /^[A-Z][A-Z0-9_]{0,63}$/;

export type { BindTriggerRequest, FlowBindingKind, FlowBindingView, FlowBindingsView };
